import React from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { NON_DEFAULT_LOCALES, stripLocaleFromPathname } from '../i18n/locale';
import SERVICE_COMING_SOON_PAGES from '../pages/products/serviceComingSoonPages';

/** Locale-prefixed blog posts → English canonical (keeps :slug). */
function BlogPostLocaleRedirect() {
    const { slug } = useParams();
    return <Navigate to={`/blog/content/${slug || ''}`} replace />;
}

/** Any locale-prefixed path → same path without locale prefix. */
function StripLocaleRedirect() {
    const location = useLocation();
    const bare = stripLocaleFromPathname(location.pathname || '/');
    return <Navigate to={`${bare}${location.search || ''}`} replace />;
}

/** English-only dynamic segment routes (e.g. /services/vps-kvm/:country). */
function englishOnlyParamRoutes(path, element) {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return (
        <React.Fragment key={`en-only-param-${normalized}`}>
            <Route path={normalized} element={element} />
            {NON_DEFAULT_LOCALES.map((locale) => (
                <Route
                    key={`en-only-param-redirect-${locale}${normalized}`}
                    path={`/${locale}${normalized}`}
                    element={<StripLocaleRedirect />}
                />
            ))}
        </React.Fragment>
    );
}

function localeRoutes(path, element) {
    if (!path || path === '/') {
        return (
            <React.Fragment key="home-routes">
                <Route path="/" element={element} />
                {/* legacy aliases: redirect instead of serving duplicate content */}
                <Route path="/index" element={<Navigate to="/" replace />} />
                <Route path="/welcome" element={<Navigate to="/" replace />} />
                {NON_DEFAULT_LOCALES.map((locale) => (
                    <React.Fragment key={`home-${locale}`}>
                        <Route path={`/${locale}`} element={element} />
                        <Route path={`/${locale}/index`} element={<Navigate to={`/${locale}`} replace />} />
                        <Route path={`/${locale}/welcome`} element={<Navigate to={`/${locale}`} replace />} />
                    </React.Fragment>
                ))}
            </React.Fragment>
        );
    }

    const normalized = path.startsWith('/') ? path : `/${path}`;
    return (
        <React.Fragment key={normalized}>
            <Route path={normalized} element={element} />
            {NON_DEFAULT_LOCALES.map((locale) => (
                <Route key={`${locale}${normalized}`} path={`/${locale}${normalized}`} element={element} />
            ))}
        </React.Fragment>
    );
}

function localeRedirect(from, to) {
    return (
        <React.Fragment key={`redirect-${from}`}>
            <Route path={from} element={<Navigate to={to} replace />} />
            {NON_DEFAULT_LOCALES.map((locale) => (
                <Route
                    key={`redirect-${locale}-${from}`}
                    path={`/${locale}${from}`}
                    element={<Navigate to={`/${locale}${to}`} replace />}
                />
            ))}
        </React.Fragment>
    );
}

/** English-only page: serve at the bare path; locale prefixes 301-style redirect to EN. */
function englishOnlyRoutes(path, element) {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return (
        <React.Fragment key={`en-only-${normalized}`}>
            <Route path={normalized} element={element} />
            {NON_DEFAULT_LOCALES.map((locale) => (
                <Route
                    key={`en-only-redirect-${locale}${normalized}`}
                    path={`/${locale}${normalized}`}
                    element={<Navigate to={normalized} replace />}
                />
            ))}
        </React.Fragment>
    );
}

export function generateAppRoutes(components) {
    const {
        Index,
        ShoppingCart,
        LegalImprint,
        LegalFairUse,
        LegalAgb,
        LegalPrivacyPolicy,
        LegalRco,
        LegalTco,
        LegalDsa,
        // Csolutions,
        RipeLir,
        Vds,
        DediServer,
        ServiceWeb,
        ServiceComingSoon,
        ServiceRemoteHands,
        ServiceManagedHosting,
        // ServiceCustomIntegrations,
        ServiceColocation,
        PageDomainDisabled,
        PageDomainNotFound,
        PageDomainNonexistent,
        GameServers,
        GameMc,
        GameCsgo,
        GameFivem,
        GameRust,
        GameTs,
        GameTerraria,
        GameArk,
        GameHytale,
        GameGarrysMod,
        GameEscapeFromTarkov,
        GameDayz,
        NetworkTransit,
        CompanyTeam,
        CompanyPartners,
        CompanyPayments,
        CompanyReviews,
        CompanyCareers,
        NetworkDProt,
        NetworkSPing,
        NetworkLGlass,
        NetworkSMonitor,
        NetworkPeering,
        NetworkSla,
        DcHardware,
        DcLocation,
        ResellerApply,
        BlogHome,
        BlogPostPage,
        PageError,
    } = components;

    return (
        <>
            {localeRoutes('/', <Index />)}
            {localeRoutes('/shoppingCart', <ShoppingCart />)}

            {localeRoutes('/legal/imprint', <LegalImprint />)}
            {localeRoutes('/legal/fair-use', <LegalFairUse />)}
            {localeRoutes('/legal/agb', <LegalAgb />)}
            {localeRoutes('/legal/privacy-policy', <LegalPrivacyPolicy />)}
            {localeRoutes('/legal/roc', <LegalRco />)}
            {localeRoutes('/legal/tco', <LegalTco />)}
            {localeRoutes('/legal/dsa', <LegalDsa />)}

            {/* /contact + /custom-solutions disabled (page not live). To restore: uncomment Csolutions in App.js and add englishOnlyRoutes here. */}

            {localeRoutes('/services/ripe-lir', <RipeLir />)}
            {localeRoutes('/services/vps-kvm', <Vds />)}
            {/* Country landings are EN-only (thin templates — avoid 378 doorway URLs). */}
            {englishOnlyParamRoutes('/services/vps-kvm/:country', <Vds />)}
            {localeRedirect('/services/vds', '/services/vps-kvm')}
            {localeRoutes('/services/dedicated-servers', <DediServer />)}
            {englishOnlyParamRoutes('/services/dedicated-servers/:country', <DediServer />)}
            {localeRoutes('/services/web-hosting', <ServiceWeb />)}

            {SERVICE_COMING_SOON_PAGES.map((page) => localeRoutes(
                `/services/${page.slug}`,
                (
                    <ServiceComingSoon
                        slug={page.slug}
                        label={page.label}
                        canonical={`/services/${page.slug}`}
                        description={page.description}
                        intro={page.intro}
                        primaryLink={page.primaryLink}
                        secondaryLink={page.secondaryLink}
                    />
                ),
            ))}

            {localeRoutes('/services/remote-hands', <ServiceRemoteHands />)}
            {localeRoutes('/services/managed-hosting', <ServiceManagedHosting />)}
            {/* {localeRoutes('/services/custom-integrations', <ServiceCustomIntegrations />)} */}
            {localeRoutes('/services/colocation', <ServiceColocation />)}
            {localeRoutes('/services/ip-transit', <NetworkTransit />)}

            {englishOnlyRoutes('/webhosting/domain-disabled', <PageDomainDisabled />)}
            {englishOnlyRoutes('/webhosting/domain-not-found', <PageDomainNotFound />)}
            {englishOnlyRoutes('/webhosting/domain-nonexistent', <PageDomainNonexistent />)}

            {localeRoutes('/game-server', <GameServers />)}
            {localeRoutes('/game-server/minecraft', <GameMc />)}
            {localeRoutes('/game-server/csgo', <GameCsgo />)}
            {localeRoutes('/game-server/fivem', <GameFivem />)}
            {localeRoutes('/game-server/rust', <GameRust />)}
            {localeRoutes('/game-server/teamspeak', <GameTs />)}
            {localeRoutes('/game-server/terraria', <GameTerraria />)}
            {localeRoutes('/game-server/ark', <GameArk />)}
            {localeRoutes('/game-server/hytale', <GameHytale />)}
            {localeRoutes('/game-server/garrys-mod', <GameGarrysMod />)}
            {localeRoutes('/game-server/escape-from-tarkov', <GameEscapeFromTarkov />)}
            {localeRoutes('/game-server/dayz', <GameDayz />)}

            {localeRoutes('/company/team', <CompanyTeam />)}
            {localeRoutes('/company/partners', <CompanyPartners />)}
            {localeRoutes('/company/payments', <CompanyPayments />)}
            {localeRoutes('/company/reviews', <CompanyReviews />)}
            {localeRoutes('/company/careers', <CompanyCareers />)}

            {localeRoutes('/network/ddos-protection', <NetworkDProt />)}
            {englishOnlyRoutes('/network/smokeping', <NetworkSPing />)}
            {localeRoutes('/network/looking-glass', <NetworkLGlass />)}
            {englishOnlyRoutes('/network/uptime', <NetworkSMonitor />)}
            {localeRoutes('/network/peering', <NetworkPeering />)}
            {localeRoutes('/network/sla', <NetworkSla />)}

            {localeRoutes('/datacenter/hardware', <DcHardware />)}
            {localeRoutes('/datacenter/location', <DcLocation />)}

            {localeRoutes('/reseller/apply-for-reseller', <ResellerApply />)}

            {localeRedirect('/company', '/company/team')}

            {englishOnlyRoutes('/blog', <BlogHome />)}
            <Route path="/blog/home" element={<Navigate to="/blog" replace />} />
            {NON_DEFAULT_LOCALES.map((locale) => (
                <Route
                    key={`blog-home-redirect-${locale}`}
                    path={`/${locale}/blog/home`}
                    element={<Navigate to="/blog" replace />}
                />
            ))}
            <Route path="/blog/content/:slug" element={<BlogPostPage />} />
            {NON_DEFAULT_LOCALES.map((locale) => (
                <Route
                    key={`en-only-blog-post-${locale}`}
                    path={`/${locale}/blog/content/:slug`}
                    element={<BlogPostLocaleRedirect />}
                />
            ))}

            <Route path="*" element={<PageError errorMessage="Page Not Found" errorCode="404" />} />
        </>
    );
}
